import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { useUpdateParentMatter } from "./hooks/update-parent-matter";
import { Col, Collapse, Descriptions, Divider, Layout, Modal, Row, Space } from "antd";
import clsx from "clsx";
import React, { useContext } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { CreateMatterButton } from "~/components";
import { PageLoading } from "~/components/page-loading";
import { LoadingSkeleton } from "~/components/page-loading/skeleton/loading-skeleton";
import { GlobalContext } from "~/context/global-context";
import {
  ClientAutoComplete,
  formatMixedPricing,
  Matter,
  MatterStatusSelectorDropdown,
  NewClientValue,
  Price,
  PriceRange,
  ProposalExportButton,
  ShareMatterButton,
} from "~/design-system";
import { createTotalsTextFromMultiplePricingTypes } from "~/design-system/components/matter-editor/pricing-text-creators";
import { PricingTotals } from "~/design-system/components/matter-editor/pricing-totals";
import { ParentMatterPartsFragment } from "~/gql/graphql";
import { TaxStatus } from "~/graphql/__generated_types__/globalTypes";
import { useSettings } from "~/hooks";
import { useFilteredClientsLazy } from "~/hooks/clients";
import { ShareMatterContainer } from "../share-matter-container";
import styles from "./parent-matter-container.css";
import { ParentMatterProposalContainer } from "./parent-matter-proposal-container";
import { FIND_PARENT_MATTER_QUERY } from "./parent-matter-query";
import { groupLineTotalByMatters } from "./pricing-funcs";
import { ParentMatterPhaseItem } from "./parent-matter-phase-item";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  layout: layoutClassName,
  content: contentClassName,
  container: containerClassName,
  //   ["pricing-summary-close-button"]: pricingSummaryCloseButtonClassName,
  //   ["drawer-controls"]: drawerControlClassname,
} = styles;

const HeaderData = (title: string, values?: (number | Price | PriceRange)[] | string) => {
  return (
    <Row justify="space-between" wrap={false}>
      <Col flex="1 1 auto">{title}</Col>
      <Col flex="0 0 auto">{`${
        typeof values === "string" ? values : formatMixedPricing(values, "£")
      }`}</Col>
    </Row>
  );
};

const mapMatterToTotalsInput = (
  x: ParentMatterPartsFragment,
  excludeTax: boolean,
  taxLevelPercent: number,
  administrationCharge: number
) => {
  return {
    pricingSummary: x.priceOptions!,
    phases: x.phases,
    disbursements: x.disbursements,
    uplifts: x.uplifts,
    discounts: x.discounts,
    technologyCharges: x.technologyCharges,
    matterWork: x.work,
    pricingLevel: x.pricingLevel,
    pricingType: x.pricingType,
    excludeTax,
    taxLevelPercent,
    administrationCharge,
    waiveAdminCharge: x.waiveAdminCharge ?? false,
  };
};

const { confirm } = Modal;

const showConfirm = (cb: any, linkedMatterTitles: string[]) => {
  confirm({
    title: `Are you sure you want to change Client`,
    icon: <ExclamationCircleOutlined />,
    content: `Changing the client will unlink the following matters: ${linkedMatterTitles.join(
      ", "
    )}`,
    onOk() {
      cb();
    },
    onCancel() {},
  });
};

export const ParentMatterContainer = () => {
  const { parentMatterId } = useParams();

  if (!parentMatterId) {
    window.location.href = "/error/404";
    return null;
  }

  const PA_LABEL = VPDLabel("PracticeArea");

  const parentMatterIdParsed = Number.parseInt(parentMatterId);

  const { pmsName } = useContext(GlobalContext);

  const [showProposalPreview, setShowProposalPreview] = React.useState(false);
  const [showShareMatter, setShowShareMatter] = React.useState(false);

  const { settings } = useSettings();

  const clientFilterData = useFilteredClientsLazy(10);

  const [updateParentMatterMutation] = useUpdateParentMatter(parentMatterIdParsed);

  const { data, loading, error } = useQuery(FIND_PARENT_MATTER_QUERY, {
    variables: {
      id: parentMatterIdParsed,
    },
    nextFetchPolicy: "cache-and-network",
  });

  if (loading) return <PageLoading loading={true} />;

  if (error) {
    window.location.href = "/error/500";
    return null;
  }

  const findParentMatter = data?.findParentMatter;
  const phases = findParentMatter?.phases ?? [];

  const { taxLabel, taxLevelPercent } = settings ?? { taxLabel: "", taxLevelPercent: 0 }; // TODO JS: multi practice areas don't seem to handle multi currency yet, tax should be updated when it does
  const excludeTax =
    phases
      .filter((x) => x.includeInProposalOutput)
      ?.flatMap((x) => x.matters)
      .some((x) => x.taxStatus !== TaxStatus.None) ?? false;

  const totalsResult = createTotalsTextFromMultiplePricingTypes(
    phases
      .filter((x) => x.includeInProposalOutput)
      ?.flatMap((x) => x.matters)
      .map((x) =>
        mapMatterToTotalsInput(x, excludeTax, taxLevelPercent, settings?.administrationCharge ?? 0)
      ),
    "£"
  );

  const groupedTotalsResult = groupLineTotalByMatters(
    phases?.filter((x) => x.includeInProposalOutput)?.flatMap((x) => x.matters) ?? [],
    excludeTax,
    taxLevelPercent,
    settings?.administrationCharge ?? 0,
    settings?.currency.symbol ?? ""
  );

  const {
    subTotalExcludingVat,
    subTotalIncludingVat,
    vat,
    disbursementsTotal,
    grandTotal,
    gpm,
    upliftsTotal,
    legalFees,
  } = totalsResult;

  const totals = [
    ...(!excludeTax
      ? [
          {
            title: `Subtotal (excl. ${taxLabel})`,
            value: subTotalExcludingVat,
          },
          { title: `${taxLabel} ${taxLevelPercent}%`, value: vat },
          {
            title: `Subtotal (incl. ${taxLabel})`,
            value: subTotalIncludingVat,
          },
        ]
      : [
          {
            title: "Subtotal",
            value: subTotalIncludingVat,
          },
        ]),

    {
      title: "Disbursements",
      value: disbursementsTotal,
    },

    { title: "Total", value: grandTotal },
  ];

  return (
    <div className={containerClassName}>
      <Layout hasSider className={layoutClassName}>
        <Layout.Content className={contentClassName}>
          <header>
            <h1>{data?.findParentMatter?.title}</h1>
            <div className={clsx(styles["header-container"], styles["header-border"])}>
              <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <span style={{ marginRight: 10, fontSize: "1.5rem" }}>Status:</span>
                <MatterStatusSelectorDropdown
                  isParent={true}
                  reference={data?.findParentMatter?.reference!}
                  status={data?.findParentMatter?.status}
                  onUpdate={async ({ status }) => {
                    updateParentMatterMutation({
                      variables: {
                        updateParentMatterId: parentMatterIdParsed,
                        updateInput: {
                          status: {
                            set: status,
                          },
                        },
                      },
                      update: (cache) => {
                        cache.evict({
                          id: "ROOT_QUERY",
                          fieldName: "filteredMatters",
                        });
                      },
                    });
                  }}
                  id={data?.findParentMatter?.id ?? 0}
                  externalMatterId={null} // TODO JS: How do we handle setting parent matters live if they don't have external matters?
                  pmsName={pmsName}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: 10, fontSize: "1.5rem" }}>Client:</span>
                <div style={{ width: "400px" }}>
                  <ClientAutoComplete
                    bordered
                    value={data?.findParentMatter?.client}
                    defaultValue={data?.findParentMatter?.client}
                    onChangeClient={(client) => {
                      const linkedMatters = findParentMatter?.phases
                        .flatMap((x) => x.matters)
                        ?.filter((x) => !!x.externalMatterId)
                        .map((x) => x.title);

                      if (linkedMatters && linkedMatters.length > 0) {
                        showConfirm(
                          () =>
                            updateParentMatterMutation({
                              variables: {
                                updateParentMatterId: parentMatterIdParsed,
                                updateInput: {
                                  client: {
                                    connect: {
                                      id: client?.id,
                                    },
                                  },
                                },
                              },
                            }),
                          linkedMatters
                        );
                      } else {
                        updateParentMatterMutation({
                          variables: {
                            updateParentMatterId: parentMatterIdParsed,
                            updateInput: {
                              client: {
                                connect: {
                                  id: client?.id,
                                },
                              },
                            },
                          },
                        });
                      }
                    }}
                    onClientSave={function (client: NewClientValue): void {
                      updateParentMatterMutation({
                        variables: {
                          updateParentMatterId: parentMatterIdParsed,
                          updateInput: {
                            client: {
                              create: {
                                name: client.name,
                              },
                            },
                          },
                        },
                      });
                    }}
                    clientFilterData={clientFilterData}
                  />
                </div>
              </div>

              <CreateMatterButton
                client={findParentMatter?.client}
                labelOveride={`Add ${PA_LABEL} to proposal`}
                parentMatterId={parentMatterIdParsed}
              />
            </div>
          </header>

          {data?.findParentMatter?.phases.map((phase) => {
            return (
              <ParentMatterPhaseItem
                excludeTax={excludeTax}
                key={phase.id}
                phase={phase}
                parentMatterId={parentMatterIdParsed}
                pmsName={pmsName}
                taxLevelPercent={taxLevelPercent}
                administrationCharge={settings?.administrationCharge ?? 0}
              />
            );
          })}
        </Layout.Content>
        <Layout.Sider
          style={{
            paddingTop: 20,
            paddingLeft: 10,
            paddingRight: 10,
            border: "1px solid #e8e8e8",
            height: "100vh",
            overflowY: "scroll",
          }}
          theme="light"
          width={350}
          collapsedWidth={0}
        >
          {loading && <LoadingSkeleton count={5} loading={loading} />}
          <Descriptions style={{ paddingLeft: 5 }} size={"middle"} title="Pricing Summary">
            <Descriptions.Item span={7} label="Client">
              {findParentMatter?.client.name}
            </Descriptions.Item>
            <Descriptions.Item span={7} label="Overall GPM">
              {gpm || ""}
            </Descriptions.Item>
          </Descriptions>
          <Collapse style={{ width: "100%" }} expandIconPosition="end">
            <Collapse.Panel header={HeaderData("Legal Fees", legalFees)} key="LF">
              {Array.from(groupedTotalsResult).map(([key, value]) => (
                <div key={key}>
                  <Link to={`/matter-editor/${key}/scope`}>
                    <Row justify="space-between" wrap={false}>
                      <Col flex="1 1 auto">{value.title}</Col>
                      <Col flex="0 0 auto">{value.legalFees}</Col>
                    </Row>
                  </Link>
                </div>
              ))}
            </Collapse.Panel>
            <Collapse.Panel
              header={HeaderData("Disbursements", disbursementsTotal)}
              key="Disbursements"
            >
              {Array.from(groupedTotalsResult).map(([key, value]) => (
                <div key={key}>
                  <Link to={`/matter-editor/${key}/disbursements`}>
                    <Row justify="space-between" wrap={false}>
                      <Col flex="1 1 auto">{value.title}</Col>
                      <Col flex="0 0 auto">{value.disbursementsTotal}</Col>
                    </Row>
                  </Link>
                </div>
              ))}
            </Collapse.Panel>
            <Collapse.Panel key={"Uplifts"} header={HeaderData("Uplifts", upliftsTotal)}>
              {Array.from(groupedTotalsResult).map(([key, value]) => (
                <div key={key}>
                  <Link to={`/matter-editor/${key}/uplifts`}>
                    <Row justify="space-between" wrap={false}>
                      <Col flex="1 1 auto">{value.title}</Col>
                      <Col flex="0 0 auto">{value.upliftsTotal}</Col>
                    </Row>
                  </Link>
                </div>
              ))}
            </Collapse.Panel>
            <Collapse.Panel header={HeaderData("Discounts", upliftsTotal)} key="Discounts">
              {Array.from(groupedTotalsResult).map(([key, value]) => (
                <div key={key}>
                  <Link to={`/matter-editor/${key}/discounts`}>
                    <Row justify="space-between" wrap={false}>
                      <Col flex="1 1 auto">{value.title}</Col>
                      <Col flex="0 0 auto">{value.discountsTotal}</Col>
                    </Row>
                  </Link>
                </div>
              ))}
            </Collapse.Panel>
          </Collapse>
          <PricingTotals data={totals} />
          <Divider />
          <Space align="center" size="small">
            <ShareMatterButton onClick={() => setShowShareMatter(true)} />
            <ProposalExportButton
              disabled={
                !data?.findParentMatter?.phases
                  .filter((x) => (x.matters?.length ?? 0) > 0)
                  .some((x) => x.includeInProposalOutput)
              }
              disabledReason={"No matters selected"}
              onClick={() => setShowProposalPreview(true)}
            />
          </Space>
        </Layout.Sider>
        {showProposalPreview && (
          <ParentMatterProposalContainer
            totals={totalsResult}
            excludeTax={excludeTax}
            matter={
              data?.findParentMatter?.phases
                ?.filter((x) => x.includeInProposalOutput)
                ?.flatMap((x) => x.matters) as unknown as Matter[]
            }
            onClose={() => setShowProposalPreview(false)}
            includeTasksInProposalOutput={
              data?.findParentMatter?.includeTasksInProposalOutput ?? false
            }
            includeRatesInProposalOutput={
              data?.findParentMatter?.includeRatesInProposalOutput ?? false
            }
            brandId={1}
            parentId={parentMatterIdParsed}
            updateMatter={(opts) => updateParentMatterMutation(opts)}
          />
        )}
        {showShareMatter && (
          <ShareMatterContainer
            clientName={findParentMatter?.client.name}
            matterId={parentMatterIdParsed}
            matterTitle={"Multi Discipline Matter"}
            onClose={() => setShowShareMatter(false)}
            opts={{ isParentMatter: true }}
          />
        )}
      </Layout>
    </div>
  );
};
